import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, RefreshCw, Search, CheckCircle2, Eye, MousePointerClick, AlertTriangle, Clock, X, Filter } from "lucide-react";

const STATUS_META = {
  sent:      { label: "Sent",      color: "#8a8a8a", bg: "#f1f1f1", icon: Clock },
  delivered: { label: "Delivered", color: "#0f6f3a", bg: "#e6f4ec", icon: CheckCircle2 },
  opened:    { label: "Opened",    color: "#8a6d1a", bg: "#fbf3dc", icon: Eye },
  clicked:   { label: "Clicked",   color: "#5a4712", bg: "#f6e9be", icon: MousePointerClick },
  bounced:   { label: "Bounced",   color: "#a11414", bg: "#fbe3e3", icon: AlertTriangle },
  complained:{ label: "Complained",color: "#a11414", bg: "#fbe3e3", icon: AlertTriangle },
  failed:    { label: "Failed",    color: "#a11414", bg: "#fbe3e3", icon: AlertTriangle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.sent;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full" style={{ background: meta.bg, color: meta.color }}>
      <Icon size={10} strokeWidth={2.5} /> {meta.label}
    </span>
  );
};

const KIND_LABELS = {
  contact_internal:      "Contact · Notification",
  contact_reply:         "Contact · Auto-Reply",
  consultation_internal: "Consultation · Notification",
  consultation_reply:    "Consultation · Auto-Reply",
  sample_internal:       "Sample · Notification",
  sample_reply:          "Sample · Auto-Reply",
  trust_doc_expiry:      "Trust Doc · Expiry Reminder",
  trust_doc_expired:     "Trust Doc · Expired Alert",
  generic:               "Generic",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const AdminEmailLog = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("");

  const load = () => {
    setLoading(true);
    const params = kindFilter ? `?kind=${encodeURIComponent(kindFilter)}` : "";
    api.get(`/admin/email-log${params}`)
      .then((r) => { setRows(r.data.log || []); setTotals(r.data.totals || {}); })
      .catch(() => toast.error("Failed to load email log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [kindFilter]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/email-log/refresh-all");
      toast.success(`Refreshed ${r.data.refreshed} email statuses from Resend`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const refreshOne = async (id) => {
    try {
      await api.post(`/admin/email-log/${id}/refresh`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = `${(r.to || []).join(" ")} ${r.subject || ""} ${r.related_id || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
        <div>
          <div className="ft-eyebrow mb-2 flex items-center gap-2"><Mail size={13} className="text-[var(--ft-gold)]"/> Email Delivery Log</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Who&apos;s opening your follow-ups</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Every outbound email through Resend is logged here. Refresh a row (or the batch) to pull the latest
            delivered / opened / clicked signal so you know who&apos;s actually reading.
          </p>
        </div>
        <button onClick={refreshAll} disabled={refreshing} className="ft-btn-primary inline-flex items-center gap-2" data-testid="email-log-refresh-all">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh from Resend"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {["sent","delivered","opened","clicked","bounced","failed"].map((k) => {
          const meta = STATUS_META[k];
          return (
            <div key={k} className="ft-card" data-testid={`email-total-${k}`}>
              <div className="ft-stat-label" style={{ color: meta.color }}>{meta.label}</div>
              <div className="ft-stat-value mt-1">{totals[k] ?? 0}</div>
            </div>
          );
        })}
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <div className="p-4 border-b border-[var(--ft-border)] flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient, subject, or related ID…" className="ft-input pl-9" data-testid="email-log-search" />
          </div>
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="ft-input pl-9 pr-8" data-testid="email-log-kind-filter">
              <option value="">All kinds</option>
              {Object.entries(KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          {(q || kindFilter) && (
            <button onClick={() => { setQ(""); setKindFilter(""); }} className="text-xs font-semibold text-[var(--ft-muted)] hover:text-[var(--ft-gold)] inline-flex items-center gap-1"><X size={12}/> Clear</button>
          )}
        </div>

        <table className="ft-table" data-testid="email-log-table">
          <thead>
            <tr>
              <th>Sent</th>
              <th>Recipient</th>
              <th>Subject / Kind</th>
              <th>Status</th>
              <th>Delivered</th>
              <th>Opened</th>
              <th>Clicked</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--ft-muted)]">Loading email log…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--ft-muted)]" data-testid="email-log-empty">
                No emails logged yet. Submit a contact form or consultation to see rows populate.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} data-testid={`email-log-row-${r.id}`}>
                <td className="text-xs whitespace-nowrap">{fmt(r.created_at)}</td>
                <td>
                  <div className="text-sm font-semibold text-[var(--ft-black)]">{(r.to || []).join(", ") || "—"}</div>
                  {r.reply_to && <div className="text-[0.7rem] text-[var(--ft-muted)]">reply-to: {r.reply_to}</div>}
                </td>
                <td>
                  <div className="text-sm text-[var(--ft-black)] max-w-xs truncate" title={r.subject}>{r.subject}</div>
                  <div className="text-[0.7rem] text-[var(--ft-muted)]">{KIND_LABELS[r.kind] || r.kind}</div>
                </td>
                <td><StatusPill status={r.last_status || (r.resend_id ? "sent" : "failed")} /></td>
                <td className="text-xs">{fmt(r.delivered_at)}</td>
                <td className="text-xs">{fmt(r.opened_at)}</td>
                <td className="text-xs">{fmt(r.clicked_at)}</td>
                <td>
                  {r.resend_id && (
                    <button onClick={() => refreshOne(r.id)} title="Refresh from Resend" className="p-1.5 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-gold)] hover:bg-[var(--ft-light-gray)]" data-testid={`email-log-refresh-${r.id}`}>
                      <RefreshCw size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailLog;
